"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { PusherEvent, TodoTaskCompletedPayload, UserLangProgressPayload } from "@/lib/pusher/pusher-tools";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "@heroui/modal";
import { useUserLocale } from "@/hooks/useUserLocale";
import { Button } from "@heroui/button";
import { XpGainedDetailedBadge } from "@/components/ui/xp/XpGainedDetailedBadge";
import { EventQueueItem } from "@/providers/ServiceModalsProviderEventQueueItem";
import { loadCurrentUserLangProgress } from "@/store/actions/app";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { DEFAULT_TODO_LIST_TAG, XP_POINTS } from "@/lib/constants";
import { TodoItem } from "../Types";

export default function TodoTaskCompletedModal({ isOpen, onClose, event }: { isOpen: boolean, onClose: () => void, event: EventQueueItem }) {
    const { t, locale } = useUserLocale();

    const dispatch = useDispatch();

    const languageProgress = useSelector((state: RootState) => state.app.languageProgress);
    const todoLists = useSelector((state: RootState) => state.app.todoLists);
    const eventPayload = event.payload as TodoTaskCompletedPayload;
    const [progressStatus, setProgressStatus] = useState<UserLangProgressPayload | null>(eventPayload.progress);
    const [todoItem, setTodoItem] = useState<TodoItem | null>(null);

    const loadedLanguageProgressOnce = useRef(false);

    useEffect(() => {
        if (languageProgress && eventPayload && !eventPayload.progress && !progressStatus && !loadedLanguageProgressOnce.current) {
            loadedLanguageProgressOnce.current = true;

            // If progress info isn't included in the payload, we'll reload language progress data
            // and synthesize the progress payload here            
            loadCurrentUserLangProgress(dispatch, eventPayload.language ?? languageProgress.language, true, true).then((progress) => {
                setProgressStatus({
                    _id: progress?._id ?? "",
                    language: progress?.language ?? "",
                    experience: progress?.experience ?? 0,
                    experienceTillNextLevel: progress?.experienceTillNextLevel ?? 0,
                    rankLevel: progress?.rankLevel ?? 0,
                } as UserLangProgressPayload);
            });
        }
    }, [eventPayload, languageProgress, loadedLanguageProgressOnce]);    

    // Take the todo item from the payload, or if only _id is provided, get it from the todo lists in the store
    useEffect(() => {
        if (eventPayload?.todoItem?._id && todoLists) {
            const todoItem = todoLists[eventPayload.listId || DEFAULT_TODO_LIST_TAG]?.items.find(item => item._id.toString() === eventPayload.todoItem._id.toString());            
            setTodoItem(todoItem);
        }
    }, [eventPayload, todoLists]);

    const xpAward = useMemo(() => {
        if (todoItem == null) {
            return 0;
        }
        return todoItem?.xpAward || todoItem?.xpAwardMultiple * XP_POINTS.todo.todoCompleted || 0;
    }, [todoItem]);

    return (
        <Modal isOpen={isOpen} onOpenChange={onClose} classNames={{backdrop: 'bg-white/75 backdrop-blur-sm'}}>
            <ModalContent>
                <ModalHeader>                    
                    {t.todoLists.modals.taskCompleted.title}                    
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-4">
                        {todoItem && <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <div>{todoItem?.emoji}</div>
                                <div>{todoItem?.title}</div>
                            </div>                            
                        </div>}
                        {todoItem && progressStatus && <XpGainedDetailedBadge xpGained={xpAward} xpCurrent={progressStatus.experience} experienceTillNextLevel={progressStatus.experienceTillNextLevel} currentRankLevel={progressStatus.rankLevel} />}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className='flex items-center gap-2 justify-between w-full'>
                        <div></div>
                        <Button onPress={() => onClose()} color='success' variant='shadow'>✅  {t.common.continue}</Button>
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
